import React, { useMemo } from 'react';
import { Alert, Collapse, Empty, Table, Tag, Typography } from 'antd';

const { Text } = Typography;

/**
 * Draft against what is published, field by field.
 *
 * This is the review step, and it has to be honest about scale. The index is recomputed
 * against the current rated universe every month rather than appended to, so a normal
 * refresh legitimately moves 19-34% of already-closed periods. A diff that buried the
 * two new numbers among 160 expected ones would not be read, so new periods and label
 * changes are listed first and separately from restatements.
 */

interface Row {
  key: string;
  group: string;
  field: string;
  before: string;
  after: string;
  kind: 'new' | 'changed' | 'removed';
}

/**
 * Rich text as the sentence a reader would see.
 *
 * Comparing markup would report a change every time a tag moved, and showing markup in a
 * table cell tells you nothing about whether the words changed.
 */
const plainPreview = (html: string): string => {
  const text = html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|li|h3|h4|blockquote)>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '—';
  return text.length > 120 ? `${text.slice(0, 120)}…` : text;
};

const fmt = (v: any): string => {
  if (v === undefined || v === null) return '—';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
};

function diffSeries(
  group: string,
  before: { periods?: string[] } & Record<string, any>,
  after: { periods?: string[] } & Record<string, any>,
  valueKeys: string[],
  rows: Row[],
) {
  const beforePeriods: string[] = before?.periods ?? [];
  const afterPeriods: string[] = after?.periods ?? [];
  const beforeIndex = new Map(beforePeriods.map((p, i) => [p, i]));

  for (const [i, period] of afterPeriods.entries()) {
    const bi = beforeIndex.get(period);
    for (const key of valueKeys) {
      const av = after?.[key]?.[i];
      const bv = bi === undefined ? undefined : before?.[key]?.[bi];
      if (bi === undefined) {
        rows.push({
          key: `${group}-${period}-${key}`, group, field: `${period} ${key}`,
          before: '—', after: fmt(av), kind: 'new',
        });
      } else if (Number(av) !== Number(bv)) {
        rows.push({
          key: `${group}-${period}-${key}`, group, field: `${period} ${key}`,
          before: fmt(bv), after: fmt(av), kind: 'changed',
        });
      }
    }
  }

  for (const period of beforePeriods) {
    if (!afterPeriods.includes(period)) {
      rows.push({
        key: `${group}-${period}-removed`, group, field: period,
        before: 'present', after: 'removed', kind: 'removed',
      });
    }
  }
}

/** A closed section still has to say how much is inside it. */
const SectionLabel: React.FC<{ text: string; count: number }> = ({ text, count }) => (
  <span>
    <Text strong>{text}</Text>
    <Tag style={{ marginLeft: 8 }}>{count}</Tag>
  </span>
);

const DiffPanel: React.FC<{ published: any; draft: any; publishedVersion: number | null }> = ({
  published, draft, publishedVersion,
}) => {
  const { headline, series } = useMemo(() => {
    const headlineRows: Row[] = [];
    const seriesRows: Row[] = [];
    if (!published || !draft) return { headline: headlineRows, series: seriesRows };

    const scalar = (field: string, b: any, a: any) => {
      if (fmt(b) !== fmt(a)) {
        headlineRows.push({
          key: field, group: 'Page', field, before: fmt(b), after: fmt(a), kind: 'changed',
        });
      }
    };

    scalar('As of', published.as_of, draft.as_of);
    scalar('Section heading', published.headline_period, draft.headline_period);
    scalar('Pricing subtitle', published.pricing?.stat_subtitle, draft.pricing?.stat_subtitle);
    // Rich text as well, so it is compared and shown as its words rather than its markup.
    scalar(
      'Note under the price chart',
      plainPreview(String(published.pricing?.note ?? '')),
      plainPreview(String(draft.pricing?.note ?? '')),
    );

    for (const set of ['integrity', 'pricing'] as const) {
      const b = published.stat_cards?.[set] ?? [];
      const a = draft.stat_cards?.[set] ?? [];
      a.forEach((card: any, i: number) => {
        const prev = b[i];
        const before = prev
          ? `${prev.value}  ${prev.direction === 'down' ? '-' : '+'}${prev.change_pct}% vs ${prev.vs_label}`
          : '—';
        const after = `${card.value}  ${card.direction === 'down' ? '-' : '+'}${card.change_pct}% vs ${card.vs_label}`;
        if (before !== after) {
          headlineRows.push({
            key: `card-${set}-${card.title}`, group: 'Stat card', field: card.title,
            before, after, kind: prev ? 'changed' : 'new',
          });
        }
      });
    }

    // The commentary is one piece of rich text per chart, not a list of paragraphs.
    //
    // This used to walk them as arrays, which was right when they were arrays. Against a
    // string, `.length` is the character count and `[i]` is a character, so a nine hundred
    // character summary produced nine hundred rows, each reporting one letter as a changed
    // paragraph.
    const COMMENTARY_LABELS = {
      integrity: 'Summary beside the integrity chart',
      pricing: 'Summary beside the price chart',
    } as const;

    for (const set of ['integrity', 'pricing'] as const) {
      const before = String(published.commentary?.[set] ?? '');
      const after = String(draft.commentary?.[set] ?? '');
      if (before !== after) {
        headlineRows.push({
          key: `copy-${set}`, group: 'Commentary',
          field: COMMENTARY_LABELS[set],
          before: plainPreview(before),
          after: plainPreview(after),
          kind: before ? 'changed' : 'new',
        });
      }
    }

    diffSeries('Integrity quarterly', published.integrity?.quarterly, draft.integrity?.quarterly,
      ['issuance', 'retirement'], seriesRows);
    diffSeries('Integrity annual', published.integrity?.annual, draft.integrity?.annual,
      ['issuance', 'retirement'], seriesRows);
    diffSeries('Pricing monthly', published.pricing?.monthly, draft.pricing?.monthly,
      ['t1', 't2', 't3'], seriesRows);
    diffSeries('Pricing annual (derived)', published.pricing?.annual, draft.pricing?.annual,
      ['t1', 't2', 't3'], seriesRows);

    return { headline: headlineRows, series: seriesRows };
  }, [published, draft]);

  if (!published) {
    return <Alert type="info" showIcon message="Nothing is published yet, so there is nothing to compare against." />;
  }

  const total = headline.length + series.length;
  if (total === 0) {
    return <Empty description={`The draft is identical to published v${publishedVersion}.`} />;
  }

  const columns = [
    { title: 'Where', dataIndex: 'group', key: 'group', width: 190 },
    { title: 'Field', dataIndex: 'field', key: 'field', width: 220 },
    {
      title: `Published v${publishedVersion}`, dataIndex: 'before', key: 'before',
      render: (v: string) => <Text type="secondary">{v}</Text>,
    },
    {
      title: 'Draft', dataIndex: 'after', key: 'after',
      render: (v: string, r: Row) => (
        <Text strong type={r.kind === 'removed' ? 'danger' : undefined}>{v}</Text>
      ),
    },
    {
      title: '', dataIndex: 'kind', key: 'kind', width: 90,
      render: (k: Row['kind']) => (
        <Tag color={k === 'new' ? 'green' : k === 'removed' ? 'red' : 'blue'}>{k}</Tag>
      ),
    },
  ];

  const newPeriods = series.filter((r) => r.kind === 'new').length;

  return (
    <div>
      <Alert
        className="mb-3"
        type="info"
        showIcon
        message={`${total} change${total === 1 ? '' : 's'} against published v${publishedVersion}`}
        description={
          <span style={{ display: 'block', maxWidth: 720 }}>
          {`${headline.length} to the page copy, headings and stat cards, and ` +
          `${series.length} to the chart series (${newPeriods} of them new periods). ` +
          'Restatement of closed periods is normal: the index is recomputed against the ' +
          'current rated universe each refresh rather than appended to.'}
          </span>
        }
      />

      {/*
        Shut to begin with.

        A refresh restates every closed period, so the series table is hundreds of rows of
        expected movement. Open, it buried the save and publish buttons under a scroll of
        numbers that almost never need reading line by line. The summary above says what
        moved and by how much; this is where you go when one of those numbers looks wrong.
      */}
      <Collapse
        defaultActiveKey={[]}
        items={[
          ...(headline.length > 0 ? [{
            key: 'headline',
            label: <SectionLabel text="Copy, headings and stat cards" count={headline.length} />,
            children: (
              <Table
                size="small" rowKey="key"
                dataSource={headline} columns={columns} pagination={false}
              />
            ),
          }] : []),
          ...(series.length > 0 ? [{
            key: 'series',
            label: <SectionLabel text="Chart series" count={series.length} />,
            children: (
              <Table
                size="small" rowKey="key"
                dataSource={series} columns={columns}
                pagination={{ pageSize: 25, size: 'small' }}
              />
            ),
          }] : []),
        ]}
      />
    </div>
  );
};

export default DiffPanel;
